import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Languages, CheckCircle2 } from "lucide-react";
import { getBilingual } from "@/lib/api";

const Paragraphs = ({ text }) => (
  <div className="space-y-3 text-sm leading-relaxed text-foreground/90">
    {String(text || "").split(/\n{2,}/).filter(Boolean).map((p, i) => (
      <p key={i}>{p}</p>
    ))}
  </div>
);

const Pair = ({ left, right, langLabel }) => (
  <div className="grid md:grid-cols-2 gap-4">
    <div className="rounded-xl border border-border bg-card p-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">{langLabel}</span>
      <div className="mt-2">{left}</div>
    </div>
    <div className="rounded-xl border border-border bg-card p-4">
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">English</span>
      <div className="mt-2">{right}</div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-serif text-2xl font-bold">{title}</h2>
    {children}
  </section>
);

export const BilingualView = ({ lecture }) => {
  const [ev, setEv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const lang = lecture.language || "Original";

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");
    setEv(null);
    getBilingual(lecture.id)
      .then((data) => { if (alive) setEv(data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || "Could not prepare the English version."); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [lecture.id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="bilingual-loading">
        <Loader2 className="h-10 w-10 mx-auto animate-spin text-accent" />
        <p className="mt-4 font-serif text-xl">Preparing the English side-by-side…</p>
        <p className="mt-1 text-sm text-muted-foreground">Translating your {lang} lecture, slides, guide & quiz — this runs once, then it's saved.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="bilingual-error">
        <Languages className="h-10 w-10 mx-auto text-muted-foreground/50" />
        <p className="mt-4 text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!ev) return null;

  const oSlides = lecture.slides || [];
  const eSlides = ev.slides || [];
  const oConcepts = lecture.key_concepts || [];
  const eConcepts = ev.key_concepts || [];
  const oTerms = lecture.key_terms || [];
  const eTerms = ev.key_terms || [];
  const oCitations = lecture.citations || [];
  const eCitations = ev.citations || [];
  const oQuiz = lecture.quiz || [];
  const eQuiz = ev.quiz || [];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="bilingual-view">
      <div className="flex items-center gap-2 text-accent">
        <Languages className="h-5 w-5" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Bilingual · {lang} + English</span>
      </div>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">{lecture.title}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{lecture.book_title} · {lecture.chapters || "Whole book"}</p>

      <div className="mt-8 space-y-10">
        <Section title="Summary">
          <Pair langLabel={lang} left={<p className="text-sm leading-relaxed">{lecture.summary}</p>} right={<p className="text-sm leading-relaxed">{ev.summary}</p>} />
        </Section>

        <Section title="Lecture">
          <Pair langLabel={lang} left={<Paragraphs text={lecture.lecture} />} right={<Paragraphs text={ev.lecture} />} />
        </Section>

        {oSlides.length > 0 && (
          <Section title="Slides">
            <div className="space-y-4">
              {oSlides.map((s, i) => (
                <Pair
                  key={i}
                  langLabel={lang}
                  left={
                    <div>
                      <p className="font-semibold">{s.title}</p>
                      <ul className="mt-1 list-disc pl-5 text-sm space-y-1">{(s.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
                    </div>
                  }
                  right={
                    <div>
                      <p className="font-semibold">{eSlides[i]?.title}</p>
                      <ul className="mt-1 list-disc pl-5 text-sm space-y-1">{(eSlides[i]?.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
                    </div>
                  }
                />
              ))}
            </div>
          </Section>
        )}

        {oConcepts.length > 0 && (
          <Section title="Key Concepts">
            <div className="space-y-4">
              {oConcepts.map((c, i) => (
                <Pair
                  key={i}
                  langLabel={lang}
                  left={<div><p className="font-semibold">{c.concept}</p><p className="mt-1 text-sm">{c.explanation}</p></div>}
                  right={<div><p className="font-semibold">{eConcepts[i]?.concept}</p><p className="mt-1 text-sm">{eConcepts[i]?.explanation}</p></div>}
                />
              ))}
            </div>
          </Section>
        )}

        {oTerms.length > 0 && (
          <Section title="Key Terms">
            <div className="space-y-4">
              {oTerms.map((t, i) => (
                <Pair
                  key={i}
                  langLabel={lang}
                  left={<div><p className="font-semibold">{t.term}</p><p className="mt-1 text-sm">{t.definition}</p></div>}
                  right={<div><p className="font-semibold">{eTerms[i]?.term}</p><p className="mt-1 text-sm">{eTerms[i]?.definition}</p></div>}
                />
              ))}
            </div>
          </Section>
        )}

        {oCitations.length > 0 && (
          <Section title="Source Citations">
            <div className="space-y-4">
              {oCitations.map((c, i) => (
                <Pair
                  key={i}
                  langLabel={lang}
                  left={<div><p className="text-sm font-medium">{c.point}</p><p className="mt-1 text-xs text-muted-foreground">{c.section} {c.pages}</p></div>}
                  right={<div><p className="text-sm font-medium">{eCitations[i]?.point}</p><p className="mt-1 text-xs text-muted-foreground">{eCitations[i]?.section} {eCitations[i]?.pages}</p></div>}
                />
              ))}
            </div>
          </Section>
        )}

        {oQuiz.length > 0 && (
          <Section title="Quiz">
            <div className="space-y-4">
              {oQuiz.map((qz, i) => {
                const eq = eQuiz[i] || {};
                const correct = qz.correct_index;
                const renderOpts = (opts) => (
                  <ul className="mt-2 space-y-1 text-sm">
                    {(opts || []).map((o, j) => (
                      <li key={j} className={`flex items-start gap-1.5 ${j === correct ? "font-semibold text-primary" : ""}`}>
                        {j === correct ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" /> : <span className="w-4 shrink-0" />}
                        {o}
                      </li>
                    ))}
                  </ul>
                );
                return (
                  <Pair
                    key={i}
                    langLabel={lang}
                    left={
                      <div>
                        <p className="font-semibold text-sm">{i + 1}. {qz.question}</p>
                        {renderOpts(qz.options)}
                        {qz.explanation && <p className="mt-2 text-xs text-muted-foreground italic">{qz.explanation}</p>}
                      </div>
                    }
                    right={
                      <div>
                        <p className="font-semibold text-sm">{i + 1}. {eq.question}</p>
                        {renderOpts(eq.options)}
                        {eq.explanation && <p className="mt-2 text-xs text-muted-foreground italic">{eq.explanation}</p>}
                      </div>
                    }
                  />
                );
              })}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};
